import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, RotateCcw } from 'lucide-react';
import { findProduct } from '../../content/products';
import { mediaFor } from '../../content/chooserMedia';
import { STEPS, stepOrder, isDone, recommend, describe, optionsFor, keyOf } from '../../lib/lockChooser';
import { PICTOS_BY_STEP } from './LockPictos';
import { track } from '../../lib/shopApi';
import { ProductCard } from './ShopBits';

// The door-first walk-through, one question at a time: pick an answer and its pictures make
// way for the next question's pictures (Hugh, 17 Sep 2026). Three taps — slides or swings,
// timber or aluminium, what is on the door — and the picks; nothing about shelter, swing
// direction or the backset (Hugh, 17 Sep: normal people found six "way too much"; the size
// comes from the door photos after payment). The answers so far sit above as chips you can
// tap to go back. `brand` filters the picks to one brand (brand hubs); without it the picks
// span every brand we carry. Results render as product cards with the fitted price; the
// material travels to the product page in the link so the install question starts filled in.
const SHOWN = 3;
// Short names for the answer chips.
const SHORT = {
  slides: { slides: 'Sliding door', swings: 'Hinged door' },
  material: { aluminium: 'Aluminium', timber: 'Timber' },
  onDoor: { bore: 'Round-bore deadbolt / lever', gripset: 'Entrance gripset', nightlatch: 'Night latch', mortice: 'Mortice lock', euro: 'Euro cylinder', none: 'Blank door', other: 'Something else', hook: 'Hook lock', pull: 'Flush pull' },
};
const shortLabel = (step, v) => (SHORT[keyOf(step)] && SHORT[keyOf(step)][v]) || String(v);

// Answers as query parameters for the product page (the material).
function doorQuery(a) {
  const q = new URLSearchParams();
  if (a.material) q.set('material', a.material);
  const s = q.toString();
  return s ? `?${s}` : '';
}

export default function LockWizard({ brand = null, testId = 'lock-wizard', compact = false }) {
  const [a, setA] = useState({});
  const [entry, setEntry] = useState('all');
  const [showAll, setShowAll] = useState(false);
  const brandOf = (slug) => { const p = findProduct(slug); return p ? p.brand : null; };
  const order = stepOrder(a);
  const optionsOf = (k) => optionsFor(k, a, brand, brandOf);
  const current = order.find((k) => a[keyOf(k)] === undefined) || null;
  // A step with a single possible answer (a brand hub that only makes hinged locks) is
  // answered for the customer.
  useEffect(() => {
    if (current && optionsOf(current).length === 1) setA((s) => Object.assign({}, s, { [keyOf(current)]: optionsOf(current)[0][0] }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current]);

  const answer = (stepKey, v) => setA((s) => {
    // Answering clears every answer after that step.
    const before = stepOrder(s);
    const idx = before.indexOf(stepKey);
    const keep = new Set(before.slice(0, Math.max(idx, 0)).map(keyOf));
    const next = {};
    for (const key of Object.keys(s)) if (keep.has(key)) next[key] = s[key];
    next[keyOf(stepKey)] = v;
    return next;
  });
  const backTo = (stepKey) => setA((s) => {
    const before = stepOrder(s);
    const idx = before.indexOf(stepKey);
    const keep = new Set(before.slice(0, Math.max(idx, 0)).map(keyOf));
    const next = {};
    for (const key of Object.keys(s)) if (keep.has(key)) next[key] = s[key];
    return next;
  });
  const back = () => {
    const answered = order.filter((k) => a[keyOf(k)] !== undefined);
    if (answered.length) backTo(answered[answered.length - 1]);
  };
  const reset = () => { setA({}); setEntry('all'); setShowAll(false); };

  const done = isDone(a);
  const answersWithEntry = useMemo(() => (entry === 'all' ? a : Object.assign({}, a, { entry })), [a, entry]);
  const rec = done ? recommend(answersWithEntry) : { slugs: [], note: null };
  const recAll = done ? recommend(a) : rec;
  let picks = rec.slugs.map(findProduct).filter(Boolean);
  const brandFiltered = brand ? picks.filter((p) => p.brand === brand) : picks;
  const noBrandMatch = brand && done && picks.length > 0 && brandFiltered.length === 0;
  picks = brand ? brandFiltered : picks;
  const shown = showAll ? picks : picks.slice(0, SHOWN);
  const allPicks = recAll.slugs.map(findProduct).filter(Boolean);
  const canFilterFingerprint = allPicks.some((p) => (p.unlock || []).some((u) => /fingerprint/i.test(u)));
  const canFilterPhone = allPicks.some((p) => (p.ecosystems || []).length > 0);

  useEffect(() => {
    if (done) track('chooser_result', { brand: brand || 'all', answers: a, picks: recAll.slugs });
    setShowAll(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [done, recAll.slugs.join(',')]);

  const answered = order.filter((k) => a[keyOf(k)] !== undefined);
  // Three steps once the first answer sets the door type; before it there is no total.
  const total = a.slides ? order.length : null;
  const stepNo = answered.length + 1;
  const query = doorQuery(a);

  return (
    <div className={`rounded-2xl border border-gray-200 bg-gray-50 ${compact ? 'p-4 sm:p-5' : 'p-5 sm:p-8'}`} data-testid={testId}>
      {answered.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-5" data-testid={`${testId}-answers`}>
          {answered.map((k) => (
            <button key={k} type="button" onClick={() => backTo(k)} className="inline-flex items-center gap-1 rounded-full bg-white border border-gray-300 px-3 py-1 text-sm text-gray-800 hover:border-gray-500" title="Change this answer">
              {shortLabel(k, a[keyOf(k)])} <span className="text-gray-400">✕</span>
            </button>
          ))}
        </div>
      )}

      {!done && current && (
        <div data-testid={`${testId}-step-${current}`}>
          <p className={`font-semibold text-gray-900 ${compact ? 'text-lg mb-3' : 'text-xl sm:text-2xl mb-4'}`}>
            <span className="text-sm font-semibold uppercase tracking-widest text-red-600 block mb-1">Step {stepNo}{total ? ` of ${total}` : ''}</span>
            {STEPS[current].q}
          </p>

          <div className={`grid gap-4 ${optionsOf(current).length <= 2 ? 'grid-cols-2' : optionsOf(current).length <= 4 ? 'grid-cols-2 lg:grid-cols-4' : 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4'}`} data-testid={`${testId}-options`}>
              {optionsOf(current).map(([v, label]) => {
                const media = mediaFor(current, v);
                const Picto = PICTOS_BY_STEP[current] && PICTOS_BY_STEP[current][v];
                const big = optionsOf(current).length <= 2 && !compact;
                return (
                  <button key={String(v)} type="button" onClick={() => answer(current, v)} className="group rounded-2xl border-2 border-gray-200 bg-white overflow-hidden text-left transition-all hover:border-blue-500 hover:shadow-lg hover:-translate-y-0.5" data-testid={`${testId}-option-${v}`}>
                    {media ? (
                      <img src={media.sm || media.src} srcSet={media.sm ? `${media.sm} 600w, ${media.src} 1200w` : undefined} sizes={big ? '(min-width: 1024px) 560px, 50vw' : '(min-width: 1024px) 280px, 50vw'} alt={media.alt} style={media.pos ? { objectPosition: media.pos } : undefined} className={`w-full object-cover ${big ? 'h-44 sm:h-56' : compact ? 'h-28' : 'h-40'}`} loading="eager" />
                    ) : Picto ? (
                      <div className={`flex items-center justify-center bg-white ${big ? 'py-6 [&_svg]:w-40 [&_svg]:h-40' : compact ? 'py-2' : 'py-4 [&_svg]:w-28 [&_svg]:h-28'}`}><Picto /></div>
                    ) : null}
                    <span className={`block text-center font-semibold text-gray-900 group-hover:text-blue-700 ${big ? 'px-4 py-3 text-lg' : compact ? 'px-2 py-2 text-sm' : 'px-3 py-3 text-base leading-snug'}`}>{label}</span>
                  </button>
                );
              })}
          </div>

          {answered.length > 0 && (
            <button type="button" onClick={back} className="mt-5 text-sm text-gray-600 inline-flex items-center gap-1 hover:text-gray-900">
              <ArrowLeft size={14} /> Back
            </button>
          )}
        </div>
      )}

      {brand && order.some((k) => optionsOf(k).length === 1) && (
        <p className="text-sm text-gray-600 mb-4" data-testid={`${testId}-given`}>
          {order.filter((k) => optionsOf(k).length === 1).map((k) => optionsOf(k)[0][1]).join(' · ')} — the only option {brand} makes.
        </p>
      )}

      {done && picks.length > 0 && (
        <div data-testid={`${testId}-recommendation`}>
          <p className="text-sm text-gray-600">{describe(a)}</p>
          <p className="text-xl sm:text-2xl font-bold text-gray-900 mt-1 mb-3">
            Our pick: {picks[0].shortName}
            {picks.length > 1 ? <span className="text-base font-normal text-gray-600"> — and {picks.length - 1} that also {picks.length === 2 ? 'fits' : 'fit'}</span> : null}
          </p>
          {(canFilterFingerprint || canFilterPhone) && (
            <div className="flex flex-wrap items-center gap-2 mb-4 text-sm" data-testid={`${testId}-entry-filter`}>
              <span className="text-gray-600">Show:</span>
              {[['all', 'Everything that fits'], canFilterFingerprint && ['fingerprint', 'Fingerprint'], canFilterPhone && ['phone', 'Apple Home / Google / Alexa']].filter(Boolean).map(([v, label]) => (
                <button key={v} type="button" onClick={() => { setEntry(v); setShowAll(false); }} className={`rounded-full border px-3 py-1.5 ${entry === v ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700 border-gray-300 hover:border-gray-500'}`} aria-pressed={entry === v}>
                  {label}
                </button>
              ))}
            </div>
          )}
          <div className={`grid gap-4 ${compact ? 'sm:grid-cols-2' : 'sm:grid-cols-2 xl:grid-cols-3'}`}>
            {shown.map((p) => (
              <ProductCard key={p.slug} product={p} query={query} />
            ))}
          </div>
          {picks.length > SHOWN && (
            <button type="button" onClick={() => setShowAll((v) => !v)} className="mt-4 px-4 py-2 rounded-full text-sm font-semibold border border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white" data-testid={`${testId}-more`}>
              {showAll ? 'Show fewer' : `Show the other ${picks.length - SHOWN} that fit`}
            </button>
          )}
          {rec.note && <p className="text-sm text-gray-700 mt-3">{rec.note}</p>}
          <div className="flex flex-wrap items-center gap-4 mt-4 text-sm">
            <button type="button" onClick={reset} className="text-gray-700 inline-flex items-center gap-1 hover:text-gray-900">
              <RotateCcw size={14} /> Start again
            </button>
          </div>
        </div>
      )}
      {done && picks.length === 0 && entry !== 'all' && !noBrandMatch && (
        <p className="text-sm text-gray-700 mt-2" data-testid={`${testId}-filter-empty`}>
          Nothing that fits this door has that. <button type="button" onClick={() => setEntry('all')} className="text-red-600 font-semibold">Show everything that fits</button>
        </p>
      )}
      {noBrandMatch && (
        <p className="text-sm text-gray-700 mt-4" data-testid={`${testId}-other-brand`}>
          {brand} does not make a lock for that door yet. <Link to="/shop/find-my-lock" className="text-red-600 font-semibold">See what does fit</Link>.
        </p>
      )}
      {done && picks.length === 0 && entry === 'all' && !noBrandMatch && (
        <div className="mt-2 rounded-xl border border-gray-200 bg-white p-4" data-testid={`${testId}-photos-instead`}>
          {rec.note && <p className="text-sm text-gray-700 mb-2">{rec.note}</p>}
          {/* The one place the shop asks for photos before a sale: a locksmith has to pick this lock (Hugh, 17 Sep 2026). */}
          <p className="text-sm text-gray-900 font-semibold">This door needs a locksmith's eye — <Link to={`/shop/find-my-lock/photos?about=${encodeURIComponent(describe(a))}`} className="text-red-600">send three photos of the door</Link>. We reply with the lock and the fitted price.</p>
          <button type="button" onClick={reset} className="mt-3 text-sm text-gray-700 inline-flex items-center gap-1 hover:text-gray-900"><RotateCcw size={14} /> Start again</button>
        </div>
      )}
    </div>
  );
}
